import { useCallback, useEffect, useRef, useState } from 'react';

const useIncrementalRender = (
  totalCount: number,
  batchSize = 1000,
): { visibleCount: number; loadMoreTriggerRef: React.RefObject<HTMLDivElement>; hasMore: boolean } => {
  const [visibleCount, setVisibleCount] = useState(batchSize);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef(0);

  useEffect(() => setVisibleCount(batchSize), [totalCount, batchSize]);

  const loadMore = useCallback(() => {
    cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = requestAnimationFrame(() => {
      setVisibleCount((prev) => Math.min(prev + batchSize, totalCount));
    });
  }, [batchSize, totalCount]);

  useEffect(() => {
    if (visibleCount >= totalCount) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { rootMargin: '0px 0px 100% 0px' },
    );

    if (loadMoreTriggerRef.current) observer.observe(loadMoreTriggerRef.current);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, [visibleCount, totalCount, loadMore]);

  return { visibleCount, loadMoreTriggerRef, hasMore: visibleCount < totalCount };
};

export default useIncrementalRender;
